import { CLERK_PUBLISHABLE_KEY } from '@/lib/env';

const RELOAD_GUARD_KEY = 'vx-chunk-reload-at';
const RELOAD_GUARD_MS = 60_000;
// The same stamp, carried in the URL. sessionStorage is preferred because it stays
// out of the address bar, but its whole job here is "survive one reload" — and a
// query param does that with no storage at all. See `reloadOnceForStaleChunk`.
const RELOAD_GUARD_PARAM = '_cr';

// Vite (native ESM) and webpack report a missing chunk with completely different
// wording, and we see both: our own lazy routes fail the Vite way, while clerk-js —
// which ships as a webpack bundle lazily pulling its own chunks from the Clerk
// frontend-API domain — fails as "ChunkLoadError: Loading chunk 344 failed". Only
// the Vite phrasings were matched here, so every Clerk chunk failure fell straight
// through this guard: no reload, no recovery, just a dead /login or /connections.
//
// A third family is the chunk that LOADS but is not JavaScript. During a Pages
// deployment swap, a request for a hashed chunk that no longer exists is answered by
// the SPA fallback — index.html — and the browser refuses to run text/html as a
// module. Each engine words that refusal differently: Safari "'text/html' is not a
// valid JavaScript MIME type", Chrome "Expected a JavaScript module script but the
// server responded with a MIME type of "text/html"", Firefox "was blocked because
// of a disallowed MIME type ("text/html")". All three are the same stale-index
// failure as a 404 and recover the same way, so the match is on the MIME-refusal
// wording, never on the content type — whatever the fallback serves is not a script.
//
// Every alternative is a literal substring: no nested quantifiers, so the test stays
// linear however long the message is.
const CHUNK_ERROR_PATTERN =
  /dynamically imported module|error loading dynamically imported|Importing a module script failed|Failed to fetch dynamically|ChunkLoadError|Loading (?:CSS )?chunk \S+ failed|is not a valid JavaScript MIME type|server responded with a MIME type of|blocked because of a disallowed MIME type/i;

// The URL marker as it stood at boot, captured by `consumeReloadMarker` before
// `stripUrlMarker` takes it back out of the address bar.
let bootMarker = 0;
let markerConsumed = false;

// Memoized: the publishable key never changes within a page life.
let clerkHostCache: string | null | undefined;

/**
 * The Clerk frontend-API host, decoded from the publishable key — Clerk encodes it
 * as base64 of "<host>$". Resolves to clerk.vertice.finance in production and to the
 * *.clerk.accounts.dev instance locally, so no domain is hardcoded and a preview
 * environment cannot be tricked by another environment's host.
 *
 * Null when the key is absent or malformed, which narrows recovery to same-origin
 * bundles rather than widening it.
 */
function clerkHost(): string | null {
  if (clerkHostCache !== undefined) return clerkHostCache;
  clerkHostCache = null;
  try {
    const encoded = CLERK_PUBLISHABLE_KEY.replace(/^pk_(?:test|live)_/, '');
    if (encoded) {
      const decoded = atob(encoded).replace(/\$+$/, '');
      // Validate through the URL parser rather than a hostname pattern: it is the same
      // parser `isRecoverableResource` compares against, so the two agree by construction,
      // and a hand-rolled host regex nests unbounded quantifiers and backtracks
      // super-linearly. Requiring the parse to round-trip rejects anything the parser had
      // to reinterpret — credentials, a port, a path, an empty host.
      const { hostname } = new URL(`https://${decoded}`);
      if (hostname && hostname === decoded.toLowerCase()) clerkHostCache = hostname;
    }
  } catch {
    // Malformed base64 — leave it null.
  }
  return clerkHostCache;
}

/** Test seam: these module memos would otherwise leak between cases. */
export function resetChunkReloadStateForTests(): void {
  clerkHostCache = undefined;
  markerConsumed = false;
  bootMarker = 0;
}

/**
 * Whether a failed resource justifies reloading the app: one of our own hashed
 * bundles, or a clerk-js chunk.
 *
 * Matched on parsed origin and hostname, never as a substring. A substring test for
 * "/assets/" or "clerk" also matches https://cdn.example.com/assets/widget.js and
 * https://evil.example.com/clerk-lookalike.js, letting any third party reload the app
 * out from under the user.
 */
export function isRecoverableResource(url: string): boolean {
  try {
    // No base: these come from HTMLScriptElement.src / HTMLLinkElement.href, which the
    // DOM has already resolved to absolute. Resolving against the page would instead
    // turn unparseable junk into a same-origin path and quietly widen the check.
    const parsed = new URL(url);
    if (parsed.origin === window.location.origin && parsed.pathname.startsWith('/assets/')) {
      return true;
    }
    const host = clerkHost();
    return host !== null && parsed.hostname.toLowerCase() === host;
  } catch {
    return false;
  }
}

/**
 * Origin and path only. Query strings and fragments carry signed tokens, redirect
 * state, and identifiers — Clerk routes `?__clerk_handshake=` and `#/?redirect_url=`
 * through exactly these URLs — and `trackException` may carry no user data.
 */
export function sanitizeResourceUrl(url: string): string | null {
  try {
    // No base: these come from HTMLScriptElement.src / HTMLLinkElement.href, which the
    // DOM has already resolved to absolute. Resolving against the page would instead
    // turn unparseable junk into a same-origin path and quietly widen the check.
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return null;
  }
}

/**
 * A failed chunk load means the running tab still holds a PREVIOUS deploy's
 * index.html while the hashed chunks it references were replaced — reloading picks up
 * the new build. This is what users see right after a deploy lands. The same recovery
 * works for a Clerk chunk that failed in transit, which is otherwise unrecoverable
 * in-page: clerk-js caches the rejected chunk promise and never retries it.
 */
export function isChunkLoadError(error: unknown): boolean {
  // webpack tags the error by name; the message alone can be empty on some browsers.
  if (error instanceof Error && error.name === 'ChunkLoadError') return true;
  const message = error instanceof Error ? error.message : String(error ?? '');
  return CHUNK_ERROR_PATTERN.test(message);
}

/**
 * The URL of the resource behind a capture-phase `error` event, or null when the
 * event is an ordinary script exception rather than a failed load.
 *
 * This is the only place a cross-origin failure is still named. A script from
 * another origin that *throws* is censored by the browser to a bare "Script error."
 * with no stack, file or line — but a script that fails to *load* dispatches an
 * error event on the element itself, where `src` is readable.
 */
export function failedResourceUrl(event: Event): string | null {
  const target = event.target;
  if (target instanceof HTMLScriptElement) return target.src || null;
  if (target instanceof HTMLLinkElement) return target.href || null;
  return null;
}

/** A distinctly named error so tracking groups these apart from app exceptions. */
export function resourceLoadError(url: string): Error {
  const error = new Error(`Failed to load ${url}`);
  error.name = 'ResourceLoadError';
  return error;
}

function readStoredStamp(): number | null {
  try {
    return Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
  } catch {
    return null; // storage blocked entirely
  }
}

function writeStoredStamp(now: number): boolean {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
    return true;
  } catch {
    return false; // readable but not writable (quota, or a silently rejecting store)
  }
}

function readUrlMarker(): number {
  try {
    const raw = new URL(window.location.href).searchParams.get(RELOAD_GUARD_PARAM);
    if (raw === null) return 0;
    const stamp = Number(raw);
    return Number.isFinite(stamp) && stamp > 0 ? stamp : 0;
  } catch {
    return 0;
  }
}

/**
 * Takes the marker back out of the visible URL once its value has been captured
 * into `bootMarker`. Runs before the router mounts, so no route ever sees the param —
 * it must not leak into deep links, `$current_url`, or Clerk's redirect handling.
 */
function stripUrlMarker(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(RELOAD_GUARD_PARAM)) return;
    url.searchParams.delete(RELOAD_GUARD_PARAM);
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    // Address bar keeps the param. Harmless beyond being visible.
  }
}

/**
 * Reads the reload marker into `bootMarker` and takes it back out of the URL.
 *
 * **Call this before `createBrowserRouter`.** React Router snapshots the location when
 * the router is *created* — at `router.tsx`'s module scope, which a static import
 * evaluates before the importing module's body — and `history.replaceState` fires no
 * `popstate`, so a later strip leaves the router holding a location that still carries
 * `_cr`. `useLocation()` would keep serving it, and `AuthAliasRedirect` re-emits
 * `search` verbatim, putting the marker back in the address bar on the /sign-in →
 * /login hop. Mounting is not the boundary that matters; router creation is.
 *
 * Idempotent: whichever caller runs first consumes the marker, and the rest are no-ops
 * that must not clobber it with the now-absent value.
 */
export function consumeReloadMarker(): void {
  if (markerConsumed) return;
  markerConsumed = true;
  bootMarker = readUrlMarker();
  stripUrlMarker();
}

function reloadWithUrlMarker(now: number): boolean {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(RELOAD_GUARD_PARAM, String(now));
    // Set before navigating so a second failure in this same page life is refused
    // even though the reload has not taken effect yet.
    bootMarker = now;
    // `replace`, not `assign`: recovery must not leave a history entry the user can
    // press Back into and re-break.
    window.location.replace(url.toString());
    return true;
  } catch {
    return false;
  }
}

/** At most one automatic reload per minute — a truly broken network must not reload-loop. */
export function reloadOnceForStaleChunk(): boolean {
  const now = Date.now();
  const stored = readStoredStamp();

  // Whichever guard is available, a stamp inside the window means we already
  // recovered once and it did not take.
  const storedStamp = stored !== null && Number.isFinite(stored) ? stored : 0;
  const lastStamp = Math.max(storedStamp, bootMarker);
  if (lastStamp > 0 && now - lastStamp < RELOAD_GUARD_MS) return false;

  // Preferred path: stamp sessionStorage and reload in place.
  if (stored !== null && writeStoredStamp(now)) {
    window.location.reload();
    return true;
  }

  // Storage is missing or read-only. This used to mean "refuse to recover at all", on
  // the reasoning that an unguarded reload could loop forever. But the stamp does not
  // actually need storage — carried in the URL it survives the reload just the same and
  // gives the identical one-shot guarantee. That matters more than it sounds: every
  // Clerk chunk failure recorded so far came from iOS Safari, which is exactly where
  // storage gets blocked (Private Browsing, ITP, third-party cookie blocking). The old
  // branch disabled recovery on precisely the platform that needed it.
  return reloadWithUrlMarker(now);
}

/**
 * Wires every path a chunk failure can arrive on. `vite:preloadError` only covers
 * Vite's own preloads — a clerk-js chunk fails asynchronously inside Clerk's loader,
 * so it surfaces as a plain window error or an unhandled rejection and never reaches
 * either that event or the router's errorElement. Without these listeners the user is
 * left on a blank auth screen with no way forward but a manual refresh.
 *
 * `report` receives resource-load failures, which otherwise reach error tracking only
 * in their censored, unactionable form. Optional so tests and any non-browser caller
 * can install recovery without a telemetry dependency.
 *
 * Returns a teardown that detaches all of them — the app never calls it, but it keeps
 * the wiring testable without listeners leaking between cases.
 */
export function installChunkErrorRecovery(
  options: { report?: (error: Error, context: Record<string, unknown>) => void } = {},
): () => void {
  const { report } = options;

  // Normally a no-op: router.tsx has already consumed the marker by the time main.tsx's
  // body runs. Kept so recovery still works standalone, wherever it is installed from.
  consumeReloadMarker();

  const onPreloadError = (event: Event) => {
    if (reloadOnceForStaleChunk()) event.preventDefault();
  };

  const onError = (event: ErrorEvent) => {
    // event.error is absent for cross-origin script errors ("Script error."); the
    // message is deliberately not matched there — it is far too broad to reload on.
    if (isChunkLoadError(event.error)) reloadOnceForStaleChunk();
  };

  const onRejection = (event: PromiseRejectionEvent) => {
    if (isChunkLoadError(event.reason)) reloadOnceForStaleChunk();
  };

  // Resource-load failures fire ON the element and do NOT bubble, so the listener
  // above never sees them — only the capture phase does, and it is the one place a
  // failing cross-origin script is still named.
  //
  // Note this covers a script that fails to LOAD. A cross-origin script that loads
  // and then throws is a different event entirely: it reaches window.onerror as a
  // censored "Script error." with no element and no URL, so nothing here can name it.
  const onResourceError = (event: Event) => {
    const url = failedResourceUrl(event);
    // Unrecoverable resources are neither reported nor reloaded for: a third-party
    // script is out of scope for this recovery, and its URL is not ours to send.
    if (!url || !isRecoverableResource(url)) return;
    const safe = sanitizeResourceUrl(url);
    if (safe) report?.(resourceLoadError(safe), { resourceUrl: safe });
    reloadOnceForStaleChunk();
  };

  window.addEventListener('vite:preloadError', onPreloadError);
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  window.addEventListener('error', onResourceError, true);

  return () => {
    window.removeEventListener('vite:preloadError', onPreloadError);
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
    window.removeEventListener('error', onResourceError, true);
  };
}
